/**
 * NOVAPAY-116-R7 — client for payret restore / bridge status (never marks paid).
 * STAGING-LAUNCH-READINESS-002-R2-R2 — this call gates the app-boot screen (see
 * StoreV2App `bootstrapping`); it MUST be bounded or a slow Worker leaves the
 * customer stuck on "Відновлюємо замовлення…" with no way out.
 */

import { CONSENT_VERSION } from "./store-info-v2.js";
import { telegramUserId } from "./customer-profile-v2.js";
import { fetchWithTimeout } from "./fetch-timeout-v2.js";

export type PayretRestoreResult = {
  ok: boolean;
  error?: string;
  consent_accepted: boolean;
  consent_version?: string;
  consent_accepted_at?: string | null;
  order: null | {
    order_id: string;
    display_order_number: string;
    payment_status: string;
    payment_method: string | null;
    total_uah: number | null;
    contact_name: string | null;
    contact_phone: string | null;
  };
  payment: null | {
    payment_id: string;
    status: string;
    paid_at: string | null;
    provider_session_id: string | null;
    amount_minor: number | null;
  };
  route: "paid_dashboard" | "pending_dashboard" | "failed_dashboard" | "none";
};

const URL = "/api/store-v2/payret";

/** STAGING-LAUNCH-READINESS-002-R2 — accepts canonical `payret` and opaque `order_` alias.
 * CUSTOMER-R4.15 — also `docsret_` for Documents deep link (STG → order detail documents).
 */
function isPayretLikeStartParam(v: string): boolean {
  const lower = v.toLowerCase();
  return (
    lower.startsWith("payret") ||
    lower.startsWith("order_") ||
    lower.startsWith("docsret_") ||
    lower.startsWith("rmpay_") ||
    lower.startsWith("rmord_")
  );
}

export function isPaymentReminderStartParam(startParam: string | null | undefined): {
  continuePayment: boolean;
  openOrder: boolean;
} {
  const lower = String(startParam ?? "").trim().toLowerCase();
  return {
    continuePayment: lower.startsWith("rmpay_"),
    openOrder: lower.startsWith("rmord_"),
  };
}

export function readPayretStartParam(): string | null {
  try {
    const fromTg = (
      window as unknown as {
        Telegram?: { WebApp?: { initDataUnsafe?: { start_param?: string } } };
      }
    ).Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (typeof fromTg === "string" && isPayretLikeStartParam(fromTg)) return fromTg;
    const q =
      new URLSearchParams(window.location.search).get("tgWebAppStartParam") ||
      new URLSearchParams(window.location.search).get("start_param");
    if (typeof q === "string" && isPayretLikeStartParam(q)) return q;
  } catch {
    /* ignore */
  }
  return null;
}

export function isDocumentsStartParam(startParam: string | null | undefined): boolean {
  return String(startParam ?? "")
    .trim()
    .toLowerCase()
    .startsWith("docsret_");
}

export async function restorePayretContext(input?: {
  startParam?: string | null;
  order?: string | null;
  rt?: string | null;
}): Promise<PayretRestoreResult> {
  try {
    const res = await fetchWithTimeout(URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        start_param: input?.startParam ?? readPayretStartParam(),
        order: input?.order ?? null,
        rt: input?.rt ?? null,
        telegram_user_id: telegramUserId(),
        consent_version: CONSENT_VERSION,
      }),
    });
    const json = (await res.json()) as PayretRestoreResult;
    return json;
  } catch {
    return {
      ok: false,
      error: "network_error",
      consent_accepted: false,
      order: null,
      payment: null,
      route: "none",
    };
  }
}

/** Bounded resume poll delays (ms): immediate + 1s + 2s + 4s + 8s. */
export const PAYRET_RESUME_BACKOFF_MS = [0, 1000, 2000, 4000, 8000] as const;

export function scheduleBoundedRetries(fn: () => void, delays: readonly number[] = PAYRET_RESUME_BACKOFF_MS): () => void {
  const timers: number[] = [];
  for (const d of delays) {
    timers.push(window.setTimeout(fn, d));
  }
  return () => {
    for (const t of timers) window.clearTimeout(t);
  };
}
